import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, FileTextIcon, UploadIcon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { SLOT_CLASSES, SlotClass } from '../../data/demo';
const MIX_SUMMARY = (slots: {
  klass: SlotClass;
}[]) => SLOT_CLASSES.map(k => ({
  k,
  n: slots.filter(s => s.klass === k).length
})).filter(c => c.n > 0).map(c => `${c.k} ×${c.n}`).join(' · ');
export function Configure() {
  const {
    mode,
    setMode,
    candidateCount,
    setCandidateCount,
    slots,
    setSlotClass,
    resetSlots,
    cvName,
    setCvName,
    bundle
  } = useDemo();
  const selectionRationale = bundle.selectionRationale;
  const [editing, setEditing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [rationaleStep, setRationaleStep] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const showRationale = mode === 'single' && Boolean(cvName);
  useEffect(() => {
    if (!showRationale) {
      setRationaleStep(0);
      return;
    }
    if (rationaleStep >= selectionRationale.length) return;
    const t = window.setTimeout(() => setRationaleStep(s => s + 1), rationaleStep === 0 ? 350 : 250);
    return () => window.clearTimeout(t);
  }, [showRationale, rationaleStep, selectionRationale.length]);

  // Selection visibly responds to the CV: matched scenarios settle to the top as rows land.
  const orderedSlots = useMemo(() => {
    if (!showRationale) return slots;
    const landed = selectionRationale.slice(0, rationaleStep).map(r => r.slotId).filter((id): id is string => Boolean(id));
    if (landed.length === 0) return slots;
    const matched = landed.map(id => slots.find(s => s.id === id)).filter((s): s is (typeof slots)[number] => Boolean(s));
    const rest = slots.filter(s => !landed.includes(s.id));
    return [...matched, ...rest];
  }, [slots, showRationale, rationaleStep, selectionRationale]);
  const choose = (m: 'cohort' | 'single') => {
    setMode(m);
    if (m === 'single') setCandidateCount(1);else if (candidateCount < 2) setCandidateCount(3);
  };
  const attemptChange = (slotId: string, klass: SlotClass) => {
    const accepted = setSlotClass(slotId, klass);
    setRefusal(accepted ? null : 'Below the clean-case floor. An assessment that only contains errors measures paranoia, not judgement.');
  };
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-569" data-mp="26zbrxh41zlyl">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-570" data-mp="2257huozhqxta">
        <LenticMark data-id="element-571" data-mp="1egei0hfr05te" />
        <BackLink to="/demo/analysis" label="Back to analysis" data-id="element-572" data-mp="1efwnbp8qhznl" />
      </div>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-8 py-10" data-id="element-573" data-mp="19iwifhnmf2y2">
        <p className="eyebrow text-brand" data-id="element-574" data-mp="3l5gegzfhml5i">Configuration</p>
        <h1 className="mt-3 text-[28px] font-semibold tracking-tight text-ink" data-id="element-575" data-mp="3l3vjj8p9h074">
          Confirm the mode and the mix.
        </h1>
        <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-muted" data-id="element-576" data-mp="3b7ahmw8jg57i">
          This is the one screen where the choices are yours. Everything after it is the system’s
          discipline.
        </p>

        <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-id="element-577" data-mp="7qtm0wlipqqe">
          <section data-id="element-578" data-mp="cpmyqmszddpi">
            <p className="eyebrow text-muted" data-id="element-579" data-mp="cq6im926yc66">Mode</p>
            <div className="mt-4 space-y-4" data-id="element-580" data-mp="cnkc8cr1e2pj">
              <button type="button" onClick={() => choose('cohort')} className={`w-full rounded-sm border bg-white p-5 text-left transition-all duration-150 ease-settle ${mode === 'cohort' ? 'border-brand opacity-100 ring-1 ring-brand/40' : 'border-rule opacity-60 hover:opacity-100'}`} data-id="element-581" data-mp="3uvt2x2qfaygx">
                <div className="flex items-center justify-between" data-id="element-582" data-mp="33pm8kh5qrzuk">
                  <h2 className="text-[15px] font-semibold text-ink" data-id="element-583" data-mp="2yxm88nlrf4xg">Cohort</h2>
                  {mode === 'cohort' && <span className="font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-584" data-mp="2tzt03p0wc0uj">
                      selected
                    </span>}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted" data-id="element-585" data-mp="3mmdt0tx0sl07">
                  One locked instrument, {Math.max(2, candidateCount)} candidates, comparable
                  results.
                </p>
                <p className="mt-3 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-586" data-mp="3cnpn2tib7wcv">
                  Library version pins when you proceed and holds until the requisition closes.
                </p>
              </button>

              <button type="button" onClick={() => choose('single')} className={`w-full rounded-sm border bg-white p-5 text-left transition-all duration-150 ease-settle ${mode === 'single' ? 'border-brand ring-1 ring-brand/40' : 'border-rule hover:border-muted/50'}`} data-id="element-587" data-mp="1x8s7x1pnz7im">
                <div className="flex items-center justify-between" data-id="element-588" data-mp="lq38dlx16x95">
                  <h2 className="text-[15px] font-semibold text-ink" data-id="element-589" data-mp="qj64o1t70kt1">Single candidate</h2>
                  {mode === 'single' && <span className="font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-590" data-mp="vjbst8097p6d">
                      selected
                    </span>}
                </div>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted" data-id="element-591" data-mp="14ft3ka4phl1l">
                  A tailored probe for one person — exec diligence, contractor verification.
                  Comparability is moot by design.
                </p>

                {mode === 'single' && <div className="mt-4" data-id="element-592" data-mp="3b8luatgoe3h3">
                    {cvName ? <span className="inline-flex items-center gap-2 rounded-sm border border-rule bg-paper px-3 py-2 font-mono-num text-[11px] text-ink" data-id="element-593" data-mp="iewsqg7mik0n">
                        <FileTextIcon className="h-3.5 w-3.5 text-brand" data-id="element-594" data-mp="rejtczsj3lsn" />
                        {cvName} · parsed
                        <button type="button" aria-label="Remove CV" onClick={e => {
                    e.stopPropagation();
                    setCvName(null);
                  }} className="text-muted transition-colors hover:text-ink" data-id="element-595" data-mp="meyrohlz4zc8">
                          <XIcon className="h-3 w-3" data-id="element-596" data-mp="162xsp0qqks4g" />
                        </button>
                      </span> : <div role="button" tabIndex={0} onClick={e => {
                  e.stopPropagation();
                  fileRef.current?.click();
                }} onKeyDown={e => {
                  if (e.key === 'Enter') fileRef.current?.click();
                }} className="flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-dashed border-rule bg-paper px-4 py-6 text-[13px] text-muted transition-colors duration-150 ease-settle hover:border-brand/60 hover:text-ink" data-id="element-597" data-mp="1k3qp5r0llxa7">
                        <UploadIcon className="h-4 w-4" data-id="element-598" data-mp="1kqy5pu48jrf5" />
                        Upload CV
                      </div>}
                    <input ref={fileRef} type="file" className="hidden" onChange={e => {
                  if (e.target.files?.[0]) setCvName('CV-0417');
                }} data-id="element-599" data-mp="15zfknhwir6ox" />
                    <p className="mt-3 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-600" data-mp="11404ukeyb0pv">
                      CV tailors selection and surface from the gated library. It never generates
                      content.
                    </p>
                  </div>}
              </button>
            </div>

            {showRationale && <div className="mt-8" data-id="element-601" data-mp="12jw3nx4ivpwi">
                <p className="eyebrow text-muted" data-id="element-602" data-mp="12ms4pubo2up6">Selection rationale</p>
                <div className="mt-4 border-t border-rule" data-id="element-603" data-mp="yk55378r85hf">
                  <div className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] gap-3 border-b border-rule py-2.5 font-mono-num text-[10px] uppercase tracking-[0.1em] text-muted/70" data-id="element-604" data-mp="tru4012a2dna">
                    <span data-id="element-605" data-mp="jug0f21gdjce">Claimed</span>
                    <span data-id="element-606" data-mp="jvhfripi4rzx" />
                    <span data-id="element-607" data-mp="js3bu616waqd">Selected from library</span>
                  </div>
                  {selectionRationale.map((r, i) => <motion.div key={r.claimed} initial={false} animate={{
                opacity: i < rationaleStep ? 1 : 0,
                y: i < rationaleStep ? 0 : 4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)] items-start gap-3 border-b border-rule/70 py-3 font-mono-num text-[11.5px] leading-relaxed" data-id="element-608" data-mp="asq1zc95lk54" data-mpi={i}>
                      <span className={r.declined ? 'text-muted' : 'text-ink'} data-id="element-609" data-mp="22qj4j8xyjrya">{r.claimed}</span>
                      <span className={r.declined ? 'text-rule' : 'text-brand'} data-id="element-610" data-mp="27pmacvlri27b">
                        {r.declined ? '' : '→'}
                      </span>
                      <span className={r.declined ? 'text-muted' : 'text-brand'} data-id="element-611" data-mp="2hijut7abe18k">{r.selected}</span>
                    </motion.div>)}
                </div>
              </div>}
          </section>

          <section data-id="element-612" data-mp="1ozmki6i3kj2u">
            <div className="flex items-baseline justify-between" data-id="element-613" data-mp="1oyurn3fvwzg9">
              <p className="eyebrow text-muted" data-id="element-614" data-mp="7wqzlqdlsxtq">Scenario mix</p>
              <button type="button" onClick={() => {
              setEditing(!editing);
              setRefusal(null);
            }} className="font-mono-num text-[11px] uppercase tracking-[0.12em] text-brand underline decoration-rule underline-offset-4 transition-colors hover:decoration-brand" data-id="element-615" data-mp="cw3tgv2w755t">
                {editing ? 'done' : 'adjust'}
              </button>
            </div>

            <p className="mt-3 font-mono-num text-[11.5px] text-ink" data-id="element-616" data-mp="3q9fcahgqjxlw">{MIX_SUMMARY(slots)}</p>

            <ul className="mt-4 space-y-2" data-id="element-617" data-mp="3v8jj51g6tj92">
              {orderedSlots.map(slot => <motion.li layout transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} key={slot.id} className={`flex items-center gap-4 rounded-sm border border-rule bg-white px-4 py-3 ${slot.klass === 'CLEAN' ? 'border-l-2 border-l-brand' : ''}`} data-id="element-618" data-mp="3x9pgjn8fci8">
                  <span className="font-mono-num text-[11px] text-muted" data-id="element-619" data-mp="1kpnaojcsex8u">{slot.id}</span>
                  {editing ? <select aria-label={`Class for ${slot.id}`} value={slot.klass} onChange={e => attemptChange(slot.id, e.target.value as SlotClass)} className="rounded-sm border border-rule bg-paper px-2 py-1 font-mono-num text-[11px] uppercase tracking-wider text-ink focus:border-brand focus:outline-none" data-id="element-620" data-mp="1frc20by8ass7">
                      {SLOT_CLASSES.map(k => <option key={k} value={k} data-id="element-621" data-mp="s0y5a84p85ra">
                          {k}
                        </option>)}
                    </select> : <span className="w-[124px] shrink-0 font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-brand" data-id="element-622" data-mp="i2ifq9lbl4jw">
                      {slot.klass}
                    </span>}
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-623" data-mp="ddr58xgr4s76">{slot.title}</span>
                  <span className="font-mono-num text-[11px] text-muted/80" data-id="element-624" data-mp="3vjw0ltwb1x9w">{slot.row}</span>
                </motion.li>)}
            </ul>

            <p className="mt-4 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-625" data-mp="r4cnql2an04j">
              Scope shown, not solutions. What each scenario contains stays sealed until the record.
            </p>
            <button type="button" onClick={() => navigate('/demo/pipeline', {
            state: {
              openKey: true
            }
          })} className="mt-1.5 font-mono-num text-[11px] text-brand underline decoration-rule underline-offset-4 transition-colors duration-150 ease-settle hover:decoration-brand" data-id="element-626" data-mp="icaqevnitzyh">
              Full key available after generation.
            </button>

            {refusal && <p className="mt-3 rounded-sm border-l-2 border-signal-red bg-white px-4 py-3 text-[13px] leading-relaxed text-ink" data-id="element-627" data-mp="2zjauknfxwyqd">
                {refusal}
              </p>}

            <p className="mt-4 max-w-lg text-[13px] leading-relaxed text-muted" data-id="element-628" data-mp="39af7lhm229ik">
              Mix is set by the role profile. At least a third clean — that floor is what makes
              appropriate trust scoreable.
            </p>
            {editing && <button type="button" onClick={() => {
            resetSlots();
            setRefusal(null);
          }} className="mt-3 font-mono-num text-[11px] uppercase tracking-[0.12em] text-muted underline decoration-rule underline-offset-4 hover:text-ink" data-id="element-629" data-mp="2bmeusnzhpow3">
                reset to profile mix
              </button>}
          </section>
        </div>

        <div className="mt-12 flex justify-end" data-id="element-630" data-mp="tkdaqjbpxg3">
          <button type="button" onClick={() => navigate('/demo/pipeline')} className="group inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-631" data-mp="26imfmfwr9j61">
            Generate and gate
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-632" data-mp="2kinooin6bwwl" />
          </button>
        </div>
      </main>

      <DemoFooter data-id="element-633" data-mp="2uglbd1pdxln3" />
    </div>;
}